/* eslint-disable @typescript-eslint/naming-convention */
import { openToastr } from '@Common/handlers/services';
import { useLatestRef, useMountedRef, usePrevious } from '@Common/hooks';
import { useState, useEffect } from 'react';
import { ErrorLoadingProjectListMsg } from 'src/researcher/constants/message.constants';
import { useDialogDispatch, closeDialogAction } from 'src/researcher/context/ui/dialog';
import { listProjectsAPI } from 'src/researcher/handlers/api';
import { AsyncStatusEnum } from 'src/researcher/types/common';
import { ListProjectAPIResponse } from 'src/researcher/types/project';
import { Loadable } from 'src/researcher/component/common/Loadable';
import { isNumber } from 'src/researcher/utils';
import { useRecentTrashProjectState, useRecentTrashProjectDispatch } from './list.store';
import {
  resetStoreListStoreAction,
  setLoadingStatusListStoreAction,
  addProjectsListStoreAction,
  loadNextListStoreAction,
} from './list.reducer';
import { DSEIfSliderVisible } from './hooks';
import { Loader } from './common';

export type TrashListDataLoadable = React.PropsWithChildren<any>;

export function TrashListDataLoadable(props: TrashListDataLoadable) {
  const dispatch = useRecentTrashProjectDispatch();
  const dialogDispatch = useDialogDispatch();

  /**
   * query, skip can be changed to load data
   */
  const query = useRecentTrashProjectState((state) => state.trash.query);
  const skip = useRecentTrashProjectState((state) => state.trash.skip);
  const forcedReloadCount = useRecentTrashProjectState((state) => state.trash.forceReloadCount);
  // const prevForcedReloadCount = usePrevious(forcedReloadCount);
  // const forced =
  //   isNumber(prevForcedReloadCount) &&
  //   isNumber(forcedReloadCount) &&
  //   forcedReloadCount &&
  //   (prevForcedReloadCount as unknown as number) < forcedReloadCount;
  // console.log({ forced });
  /**
   * Mounted ref needs to be be there to reset any state that already exist before mounting
   */
  const isMountedRef = useMountedRef();
  /**
   * Zombie store happens sometimes thus we reset store on mount
   */
  useEffect(() => {
    if (isMountedRef) {
      /**
       * Due to Zombie Store issue in redux we first clear store on every mount
       */
      dispatch(resetStoreListStoreAction('trash'));
    }
  }, []);

  /**
   * during first loading
   * everything must be visible at once
   * This is because as there is create-new button on page
   * before loading the page we don't know storage used by the user
   * and if project creation is valid
   * so we must make list api call and if success show ui.
   * this ui must have used storage alert
   * */
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(true);

  useEffect(() => {
    dispatch(setLoadingStatusListStoreAction(AsyncStatusEnum.PENDING, 'trash'));
    let ignore = false;
    listProjectsAPI({
      query,
      skip,
      limit: 16,
      status: 'trash',
    })
      .then((data: ListProjectAPIResponse) => {
        if (ignore) {
          return;
        }
        // update the state
        // with the new projects list
        dispatch(addProjectsListStoreAction(data, 'trash'));
      })
      .catch(() => {
        if (ignore) {
          return;
        }
        dialogDispatch(closeDialogAction());
        dispatch(setLoadingStatusListStoreAction(AsyncStatusEnum.REJECT, 'trash'));
        openToastr(ErrorLoadingProjectListMsg);
      })
      .finally(() => {
        if (ignore) {
          return;
        }
        dialogDispatch(closeDialogAction());
        setIsLoadingFirstPage(false);
      });

    return () => {
      ignore = true;
    };
  }, [query, skip, forcedReloadCount]);

  // eslint-disable-next-line no-underscore-dangle
  const _load_next_ = () => dispatch(loadNextListStoreAction('trash'));
  const loadNextRef = useLatestRef(_load_next_);

  return (
    <Loadable loader={<Loader sliderId="trash-bin" />} loading={isLoadingFirstPage}>
      <DSEIfSliderVisible offset={150} sliderId="trash-bin" onScrollEnd={loadNextRef}>
        {props.children}
      </DSEIfSliderVisible>
    </Loadable>
  );
}
